import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@/lib/router";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import {
  metricProposalsApi,
  type MetricProposal,
  type CatalogValidationReport,
  type MetricValidationResult,
} from "../api/metricProposals";
import { agentDashboardsApi, type WidgetValidationReport } from "../api/agentDashboards";
import { usePractice } from "../context/PracticeContext";
import { PageSkeleton } from "../components/PageSkeleton";
import { EmptyState } from "../components/EmptyState";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles,
  Check,
  X,
  ChevronDown,
  ChevronRight,
  Bot,
  Loader2,
  LayoutDashboard,
  ShieldCheck,
  AlertTriangle,
  Database,
} from "lucide-react";
import { cn } from "../lib/utils";

const QUERY_KEY = ["admin", "metric-proposals"] as const;

const CATEGORIES = [
  "Growth",
  "Operations",
  "Revenue Cycle",
  "Patient Experience",
] as const;

function severityVariant(sev: string): "default" | "secondary" | "destructive" | "outline" {
  if (sev === "critical") return "destructive";
  if (sev === "warning") return "default";
  return "secondary";
}

export function AdminMetricProposals() {
  const { setBreadcrumbs } = useBreadcrumbs();
  const queryClient = useQueryClient();
  const { selectedPracticeId } = usePractice();
  const [generateCategory, setGenerateCategory] =
    useState<(typeof CATEGORIES)[number]>("Growth");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [thresholdOverrides, setThresholdOverrides] = useState<Record<string, string>>({});
  const [feedback, setFeedback] = useState<string | null>(null);
  const [validation, setValidation] = useState<CatalogValidationReport | null>(null);
  const [widgetValidation, setWidgetValidation] = useState<WidgetValidationReport | null>(null);

  useEffect(() => {
    setBreadcrumbs([
      { label: "Administration" },
      { label: "Metric Proposals" },
    ]);
  }, [setBreadcrumbs]);

  const { data, isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => metricProposalsApi.list(),
  });

  const generate = useMutation({
    mutationFn: (category: string) => {
      if (!selectedPracticeId) throw new Error("Select a practice first.");
      return metricProposalsApi.generate(selectedPracticeId, category, 15);
    },
    onSuccess: (res) => {
      const parts = [
        `Created ${res.proposalsCreated} valid proposals for ${res.requestedCategory}, checked against ${res.liveExploresLoaded} live Looker explores.`,
      ];
      if (res.skippedAsInvalid.length > 0) {
        parts.push(`${res.skippedAsInvalid.length} dropped (referenced fields not in Looker).`);
      }
      if (res.skippedAsDuplicate.length > 0) {
        parts.push(`${res.skippedAsDuplicate.length} skipped as duplicates.`);
      }
      setFeedback(parts.join(" "));
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (e: unknown) => {
      setFeedback(
        `Generate failed: ${e instanceof Error ? e.message : String(e)}`,
      );
    },
  });

  const approve = useMutation({
    mutationFn: async (ids: string[]) => {
      const approveRes = await metricProposalsApi.approveBatch(
        ids,
        thresholdOverrides,
      );
      // After approval, regenerate per-category dashboards so the new metrics
      // immediately surface as widgets. Best-effort — surface as a warning if
      // it fails but don't roll back the approval.
      let regen: Awaited<ReturnType<typeof agentDashboardsApi.regenerate>> | null =
        null;
      let regenError: string | null = null;
      if (selectedPracticeId) {
        try {
          regen = await agentDashboardsApi.regenerate(selectedPracticeId);
        } catch (e) {
          regenError = e instanceof Error ? e.message : String(e);
        }
      }
      return { approveRes, regen, regenError };
    },
    onSuccess: ({ approveRes, regen, regenError }) => {
      let msg = `Approved ${approveRes.approved} proposals.`;
      if (regen) {
        msg += ` Regenerated ${regen.categoriesProcessed.length} category dashboard${regen.categoriesProcessed.length === 1 ? "" : "s"} (${regen.widgetsWritten} widgets).`;
      } else if (regenError) {
        msg += ` (Dashboard regeneration failed: ${regenError})`;
      }
      setFeedback(msg);
      setSelected(new Set());
      setThresholdOverrides({});
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: ["agent-dashboards", selectedPracticeId],
      });
    },
  });

  const regenerate = useMutation({
    mutationFn: async () => {
      if (!selectedPracticeId) throw new Error("No company selected");
      return agentDashboardsApi.regenerate(selectedPracticeId);
    },
    onSuccess: (res) => {
      setFeedback(
        `Regenerated ${res.categoriesProcessed.length} category dashboard${res.categoriesProcessed.length === 1 ? "" : "s"} (${res.dashboardsCreated} new, ${res.dashboardsUpdated} refreshed, ${res.widgetsWritten} widgets).`,
      );
      queryClient.invalidateQueries({
        queryKey: ["agent-dashboards", selectedPracticeId],
      });
    },
    onError: (e: unknown) => {
      setFeedback(
        `Regenerate failed: ${e instanceof Error ? e.message : String(e)}`,
      );
    },
  });

  const reject = useMutation({
    mutationFn: (ids: string[]) => metricProposalsApi.rejectBatch(ids),
    onSuccess: (res) => {
      setFeedback(`Rejected ${res.rejected} proposals.`);
      setSelected(new Set());
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const validate = useMutation({
    mutationFn: () => {
      if (!selectedPracticeId) throw new Error("Select a practice first.");
      return metricProposalsApi.validate(selectedPracticeId);
    },
    onSuccess: (report) => {
      setValidation(report);
      setFeedback(
        `Validated ${report.totalMetrics} metrics against Looker — ${report.validCount} valid, ${report.invalidCount} with issues.`,
      );
    },
    onError: (e) =>
      setFeedback(`Validation failed: ${e instanceof Error ? e.message : String(e)}`),
  });

  const validateWidgets = useMutation({
    mutationFn: () => {
      if (!selectedPracticeId) throw new Error("Select a practice first.");
      return agentDashboardsApi.validateWidgets(selectedPracticeId);
    },
    onSuccess: (report) => {
      if (report.error) {
        setFeedback(`Widget validation failed: ${report.error}`);
        return;
      }
      setWidgetValidation(report);
      const slow = report.byStatus.timeout ?? 0;
      const broken = report.problems.length - slow;
      setFeedback(
        `Checked ${report.totalWidgets} dashboard widgets — ${report.byStatus.ok ?? 0} OK, ${broken} need attention` +
          (slow ? `, ${slow} slow (unverified)` : "") +
          ".",
      );
    },
    onError: (e) =>
      setFeedback(`Widget validation failed: ${e instanceof Error ? e.message : String(e)}`),
  });

  // Quick lookup: metric id -> validation result (only populated after Validate).
  const resultById = useMemo(() => {
    const map = new Map<string, MetricValidationResult>();
    for (const r of validation?.results ?? []) map.set(r.id, r);
    return map;
  }, [validation]);

  // Bulk-select every proposed metric the validator flagged as broken — makes
  // "validate then reject the junk" a two-click flow.
  function selectInvalid() {
    if (!validation) return;
    const invalidIds = validation.results.filter((r) => !r.valid).map((r) => r.id);
    setSelected(new Set(invalidIds));
  }

  if (isLoading) return <PageSkeleton variant="list" />;

  const byCategory = data?.byCategory ?? {};
  const totalProposed = data?.total ?? 0;
  const categories = Object.keys(byCategory).sort();

  function toggleExpanded(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelected(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectCategory(items: MetricProposal[]) {
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = items.every((m) => next.has(m.id));
      if (allSelected) {
        for (const m of items) next.delete(m.id);
      } else {
        for (const m of items) next.add(m.id);
      }
      return next;
    });
  }

  return (
    <div className="space-y-4 p-6 max-w-7xl mx-auto">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold">Metric Proposals</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Agent Architect proposes new alert-able metrics by reading the VIP
            data dictionary. Approve or reject in batches per category.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge variant="secondary">{totalProposed} proposed</Badge>
          <Button size="sm" variant="outline" asChild>
            <Link to="/admin/looker-schema">
              <Database className="h-3.5 w-3.5 mr-1.5" />
              Looker schema
            </Link>
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setFeedback(null);
              validate.mutate();
            }}
            disabled={validate.isPending || !selectedPracticeId}
            title="Check every metric's Looker fields against the live semantic layer"
          >
            {validate.isPending ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
            )}
            Validate against Looker
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setFeedback(null);
              validateWidgets.mutate();
            }}
            disabled={validateWidgets.isPending || !selectedPracticeId}
            title="Run every dashboard widget's query — flags widgets that error or return no data"
          >
            {validateWidgets.isPending ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <LayoutDashboard className="h-3.5 w-3.5 mr-1.5" />
            )}
            Validate widgets
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => regenerate.mutate()}
            disabled={regenerate.isPending || !selectedPracticeId}
          >
            {regenerate.isPending ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <LayoutDashboard className="h-3.5 w-3.5 mr-1.5" />
            )}
            Regenerate dashboards
          </Button>
        </div>
      </div>

      {/* Generate row */}
      <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-4">
        <Sparkles className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Propose metrics for</span>
        <select
          className="rounded-md border border-input bg-background px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
          value={generateCategory}
          onChange={(e) =>
            setGenerateCategory(e.target.value as (typeof CATEGORIES)[number])
          }
          disabled={generate.isPending}
        >
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <Button
          onClick={() => {
            setFeedback(null);
            generate.mutate(generateCategory);
          }}
          disabled={generate.isPending || !selectedPracticeId}
          size="sm"
        >
          {generate.isPending ? (
            <>
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              Asking Agent Architect…
            </>
          ) : (
            <>
              <Bot className="h-3.5 w-3.5 mr-1.5" />
              Generate proposals
            </>
          )}
        </Button>
      </div>

      {feedback && (
        <div className="rounded-md border border-border bg-muted/30 px-4 py-2 text-sm">
          {feedback}
        </div>
      )}

      {/* Validation summary */}
      {validation && (
        <div className="rounded-lg border border-border bg-card p-4 text-sm space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-medium">Looker validation</span>
            <Badge variant="secondary">{validation.validCount} valid</Badge>
            {validation.invalidCount > 0 && (
              <Badge variant="destructive">{validation.invalidCount} with issues</Badge>
            )}
            <span className="text-xs text-muted-foreground">
              {validation.totalMetrics} metrics · {validation.exploresChecked} explores · checked{" "}
              {new Date(validation.checkedAt).toLocaleString()}
            </span>
            {validation.invalidCount > 0 && (
              <Button size="sm" variant="ghost" onClick={selectInvalid}>
                Select all broken (proposed)
              </Button>
            )}
          </div>
          {validation.brokenExplores.length > 0 && (
            <div className="text-xs text-destructive">
              Explores not found in Looker: {validation.brokenExplores.join(", ")}
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Validation covers proposed + approved metrics. Badges below mark each proposal; expand a
            row to see which fields don't exist.
          </p>
        </div>
      )}

      {/* Widget validation results */}
      {widgetValidation && (
        <div className="rounded-lg border border-border bg-card p-4 text-sm space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-medium">Dashboard widget validation</span>
            <Badge variant="secondary">{widgetValidation.byStatus.ok ?? 0} OK</Badge>
            {(widgetValidation.byStatus.no_data ?? 0) > 0 && (
              <Badge variant="default">{widgetValidation.byStatus.no_data} no data</Badge>
            )}
            {((widgetValidation.byStatus.error ?? 0) + (widgetValidation.byStatus.no_query ?? 0)) > 0 && (
              <Badge variant="destructive">
                {(widgetValidation.byStatus.error ?? 0) + (widgetValidation.byStatus.no_query ?? 0)} broken
              </Badge>
            )}
            {(widgetValidation.byStatus.timeout ?? 0) > 0 && (
              <Badge variant="secondary">{widgetValidation.byStatus.timeout} slow</Badge>
            )}
            <span className="text-xs text-muted-foreground">
              {widgetValidation.totalWidgets} widgets · ran each query · checked{" "}
              {new Date(widgetValidation.checkedAt).toLocaleString()}
            </span>
          </div>
          {widgetValidation.problems.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Every widget returned data. Nothing to fix.
            </p>
          ) : (
            <div className="divide-y divide-border rounded-md border border-border">
              {widgetValidation.problems.map((p) => (
                <div key={p.widgetId} className="flex items-start gap-3 px-3 py-2">
                  <Badge
                    variant={
                      p.status === "no_data" || p.status === "timeout" ? "secondary" : "destructive"
                    }
                    className="shrink-0"
                  >
                    {p.status === "no_data"
                      ? "no data"
                      : p.status === "no_query"
                        ? "no query"
                        : p.status === "timeout"
                          ? "slow"
                          : "error"}
                  </Badge>
                  <div className="min-w-0">
                    <p className="font-medium truncate">
                      {p.title}{" "}
                      <span className="font-normal text-muted-foreground">· {p.dashboard}</span>
                    </p>
                    <p className="text-[11px] font-mono text-muted-foreground truncate">
                      {p.explore ?? "—"}
                    </p>
                    {p.error && p.status !== "timeout" && (
                      <p className="text-[11px] text-destructive mt-0.5 break-words">{p.error}</p>
                    )}
                    {p.status === "no_data" && (
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        Query is valid but returned 0 rows — likely a bad filter value or an empty slice.
                      </p>
                    )}
                    {p.status === "timeout" && (
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        Query exceeded the validation timeout — this explore is just slow; the widget is likely fine.
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Selection action bar */}
      {selected.size > 0 && (
        <div className="sticky top-2 z-10 flex items-center gap-3 rounded-md border border-border bg-background px-4 py-2 shadow-sm">
          <span className="text-sm font-medium">
            {selected.size} selected
          </span>
          <div className="flex-1" />
          <Button
            size="sm"
            variant="outline"
            onClick={() => reject.mutate([...selected])}
            disabled={reject.isPending}
          >
            <X className="h-3.5 w-3.5 mr-1.5" />
            Reject
          </Button>
          <Button
            size="sm"
            onClick={() => approve.mutate([...selected])}
            disabled={approve.isPending}
          >
            <Check className="h-3.5 w-3.5 mr-1.5" />
            Approve
          </Button>
        </div>
      )}

      {/* Empty state */}
      {categories.length === 0 && (
        <EmptyState
          icon={Bot}
          message="No proposals pending. Generate a batch above to get started."
        />
      )}

      {/* Categories */}
      {categories.map((category) => {
        const items = byCategory[category] ?? [];
        const allSelected = items.length > 0 && items.every((m) => selected.has(m.id));
        return (
          <div
            key={category}
            className="rounded-lg border border-border bg-card"
          >
            <div className="flex items-center gap-3 border-b border-border px-4 py-3">
              <h3 className="text-sm font-semibold">{category}</h3>
              <span className="text-xs text-muted-foreground">
                {items.length} {items.length === 1 ? "metric" : "metrics"}
              </span>
              <div className="flex-1" />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => selectCategory(items)}
              >
                {allSelected ? "Deselect all" : "Select all"}
              </Button>
            </div>
            <div className="divide-y divide-border">
              {items.map((m) => {
                const isOpen = expanded.has(m.id);
                const isSelected = selected.has(m.id);
                return (
                  <div key={m.id} className="px-4 py-3">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 rounded border-input"
                        checked={isSelected}
                        onChange={() => toggleSelected(m.id)}
                      />
                      <button
                        className="flex-1 text-left"
                        onClick={() => toggleExpanded(m.id)}
                      >
                        <div className="flex items-center gap-2">
                          {isOpen ? (
                            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                          )}
                          <span className="text-sm font-medium">{m.label}</span>
                          <Badge
                            variant={severityVariant(m.defaultSeverity)}
                            className="text-[10px] uppercase"
                          >
                            {m.defaultSeverity}
                          </Badge>
                          <span className="text-xs font-mono text-muted-foreground">
                            {m.defaultOperator} {m.defaultThreshold}
                          </span>
                          {(() => {
                            const r = resultById.get(m.id);
                            if (!r) return null;
                            return r.valid ? (
                              <Badge variant="secondary" className="gap-1 text-[10px]">
                                <ShieldCheck className="h-3 w-3" />
                                Valid
                              </Badge>
                            ) : (
                              <Badge variant="destructive" className="gap-1 text-[10px]">
                                <AlertTriangle className="h-3 w-3" />
                                {r.issues.length} {r.issues.length === 1 ? "issue" : "issues"}
                              </Badge>
                            );
                          })()}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 pl-5">
                          {m.description}
                        </p>
                      </button>
                    </div>
                    {isOpen && (
                      <div className="ml-7 mt-3 space-y-2 rounded-md bg-muted/30 p-3 text-xs">
                        <div>
                          <span className="text-muted-foreground">Key: </span>
                          <code className="font-mono">{m.key}</code>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Source: </span>
                          <code className="font-mono">
                            {m.sourceModel} → {m.sourceExplore}
                          </code>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Measure: </span>
                          <code className="font-mono">{m.measureField}</code>
                        </div>
                        {(() => {
                          const r = resultById.get(m.id);
                          if (!r || r.valid) return null;
                          return (
                            <div className="rounded border border-destructive/30 bg-destructive/5 p-2 space-y-1">
                              <div className="flex items-center gap-1.5 font-medium text-destructive">
                                <AlertTriangle className="h-3.5 w-3.5" />
                                Looker validation failed
                              </div>
                              <ul className="list-disc pl-5 space-y-0.5">
                                {r.issues.map((iss, i) => (
                                  <li key={i}>{iss.detail}</li>
                                ))}
                              </ul>
                            </div>
                          );
                        })()}
                        {m.businessDefinition && (
                          <div>
                            <span className="text-muted-foreground">
                              Business definition:{" "}
                            </span>
                            {m.businessDefinition}
                          </div>
                        )}
                        {m.calculationLogic && (
                          <div>
                            <span className="text-muted-foreground">Logic: </span>
                            {m.calculationLogic}
                          </div>
                        )}
                        <div className="flex items-center gap-2 pt-1">
                          <span className="text-muted-foreground">Override threshold:</span>
                          <input
                            type="number"
                            step="any"
                            className={cn(
                              "w-24 rounded border border-input bg-background px-2 py-0.5 font-mono",
                              "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring",
                            )}
                            placeholder={m.defaultThreshold}
                            value={thresholdOverrides[m.id] ?? ""}
                            onChange={(e) =>
                              setThresholdOverrides((prev) => ({
                                ...prev,
                                [m.id]: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
